import React, { useState, useRef } from 'react';
import { Loader2, Upload, X, FileText } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ExportBrandingModal
 *
 * Asks the presenter for Shop Name + Logo BEFORE downloading the PDF.
 * The brand values flow through to `/api/export-lookbook?brand_name=...&brand_logo=...`
 * so the PDF carries the prospect's brand instead of the logged-in user's store.
 */
export default function ExportBrandingModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  defaultName = '',
  title = 'Brand this Lookbook',
  subtitle = 'Drop in the shop name + logo that should appear on the cover, headers, and footer of the exported PDF.',
  primaryColor = '#C2A878',
}) {
  const fileInputRef = useRef(null);
  const [shopName, setShopName] = useState(defaultName);
  const [logoB64, setLogoB64] = useState('');
  const [logoPreview, setLogoPreview] = useState('');

  // Reset when modal opens with a new default
  React.useEffect(() => {
    if (open) {
      setShopName(defaultName);
      setLogoB64('');
      setLogoPreview('');
    }
  }, [open, defaultName]);

  if (!open) return null;

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      setLogoPreview(dataUrl);
      setLogoB64(dataUrl.split(',')[1] || '');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
      data-testid="export-branding-modal"
    >
      <div className="bg-[#121215] border border-[#27272A] max-w-md w-full p-6 md:p-8 relative">
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-3 right-3 text-[#52525B] hover:text-[#F5F5F5] disabled:opacity-50"
          data-testid="export-branding-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: primaryColor }}>
          PDF BRANDING
        </div>
        <h3
          className="text-3xl font-light text-[#F5F5F5] mt-1 mb-2"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}
        >
          {title}
        </h3>
        <p className="text-xs text-[#A1A1AA] mb-5 italic" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '14px' }}>
          {subtitle}
        </p>

        <label className="block mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Shop Name</div>
          <input
            type="text"
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            placeholder="e.g. Windsor &amp; Co. Tailors"
            className="w-full bg-[#0A0A0C] border border-[#27272A] focus:border-[#C2A878] outline-none px-3 py-2.5 text-sm text-[#F5F5F5]"
            data-testid="export-brand-shop-name-input"
            autoFocus
          />
        </label>

        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Logo</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoChange}
            data-testid="export-brand-logo-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full border border-dashed border-[#27272A] hover:border-[#C2A878] bg-[#0A0A0C] py-4 text-xs text-[#A1A1AA] hover:text-[#F5F5F5] flex items-center justify-center gap-2 transition-colors"
            data-testid="export-brand-logo-upload"
          >
            {logoPreview ? (
              <span className="flex items-center gap-3">
                <img src={logoPreview} alt="logo preview" className="max-h-12 max-w-24 object-contain" />
                <span>Change logo</span>
              </span>
            ) : (
              <>
                <Upload className="w-3.5 h-3.5" />
                <span className="uppercase tracking-[0.2em] text-[10px]">Upload Logo</span>
              </>
            )}
          </button>
          <p className="text-[10px] text-[#52525B] mt-1.5 italic">PNG or JPG. Transparent backgrounds work best.</p>
        </div>

        <div className="flex gap-3 pt-5 border-t border-[#27272A]">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-3 border border-[#27272A] hover:border-[#52525B] text-[#A1A1AA] text-[10px] uppercase tracking-[0.22em] transition-colors disabled:opacity-50"
            data-testid="export-branding-skip"
          >
            Skip — use my store branding
          </button>
          <Button
            onClick={() => onConfirm({ brand_name: shopName.trim(), brand_logo: logoB64 })}
            disabled={loading}
            className="flex-1 rounded-none uppercase tracking-[0.15em] text-[10px] py-3 text-[#0A0A0C]"
            style={{ backgroundColor: primaryColor }}
            data-testid="export-branding-confirm"
          >
            {loading ? (
              <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Exporting…</>
            ) : (
              <><FileText className="w-3 h-3 mr-2" /> Download PDF</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
